/**
 * Pure helpers for inserting exercises ABOVE/BELOW an existing exercise of a
 * program session (save-workout-queue / paste-exercises-program `insert`).
 *
 * Keep in sync with msb-api lib/api/exercises/exercise-insert-helpers.js — the
 * server is authoritative; the GUI runs the same algorithm to preview letters.
 * Both test files share the same cases.
 *
 * Model (see msb-gui utils/prefix-calculator.js):
 *  - Exercises and normal sections share one `order` space; sections sort
 *    BEFORE an exercise with the same order. Workout notes never take part
 *    (callers pass only normal sections).
 *  - A "row" is one exercise, or all members of one complex (atomic: nothing
 *    is ever inserted inside a complex).
 *  - A circuit is a maximal chain of adjacent non-complex rows linked by
 *    `nextSuperset` (a section between two rows breaks the chain).
 *
 * Rules:
 *  - above = immediately before the target row, below = immediately after it.
 *  - Target inside a circuit: new exercises JOIN it (relinked through them),
 *    EXCEPT above the first / below the last member, where they land outside
 *    and the circuit is untouched.
 *  - Joining drops the new items' own sections and supersets (flattened).
 *  - Orders stay integers for exercises; only entries at/after the insertion
 *    point shift, and only as far as needed.
 */
import { computeSectionPrefixes } from "./prefix-calculator";
import { excludeWorkoutNotes } from "./section-types";

export const POSITIONS = ["above", "below"];

const sid = (value) => (value == null ? null : String(value));
const orderOf = (doc) => (typeof doc?.order === "number" ? doc.order : 0);
const complexKey = (exercise) =>
  exercise?.complex && exercise?.complexInstance
    ? `${exercise.complex}_${exercise.complexInstance}`
    : null;

/** @returns {string|null} error message, or null when `insert` is well formed */
export const validateInsert = (insert) => {
  if (insert === undefined || insert === null) return null;
  if (typeof insert !== "object") return "insert must be an object";
  if (!insert.targetExerciseId) return "insert.targetExerciseId is required";
  if (!POSITIONS.includes(insert.position)) {
    return 'insert.position must be "above" or "below"';
  }
  return null;
};

/**
 * Sorted entries (exercises + normal sections) of a session.
 * Ties: a section sorts before an exercise (same as groupExercisesBySection).
 */
const buildEntries = (exercises, sections) => {
  const entries = [
    ...exercises.map((doc) => ({ kind: "exercise", doc, order: orderOf(doc) })),
    ...excludeWorkoutNotes(sections).map((doc) => ({
      kind: "section",
      doc,
      order: orderOf(doc),
    })),
  ];
  entries.forEach((entry, i) => {
    entry.seq = i;
  });
  entries.sort(
    (a, b) =>
      a.order - b.order ||
      (a.kind === b.kind ? 0 : a.kind === "section" ? -1 : 1) ||
      a.seq - b.seq
  );
  return entries;
};

/** Group exercise entries into rows (complex members collapse into one). */
const buildRows = (entries) => {
  const rows = [];
  const byComplex = new Map();
  entries.forEach((entry, index) => {
    if (entry.kind !== "exercise") return;
    const key = complexKey(entry.doc);
    if (key && byComplex.has(key)) {
      const row = byComplex.get(key);
      row.ids.push(sid(entry.doc._id));
      row.docs.push(entry.doc);
      row.lastIdx = index;
      return;
    }
    const row = {
      ids: [sid(entry.doc._id)],
      docs: [entry.doc],
      isComplex: Boolean(key),
      firstIdx: index,
      lastIdx: index,
    };
    if (key) byComplex.set(key, row);
    rows.push(row);
  });
  return rows;
};

/** True when row `b` directly follows row `a` inside one circuit. */
const isLinked = (a, b) =>
  !a.isComplex &&
  !b.isComplex &&
  b.firstIdx === a.lastIdx + 1 &&
  Boolean(a.docs[0].nextSuperset) &&
  sid(a.docs[0].nextSuperset) === b.ids[0];

/** Rows of the circuit containing `rows[rowIndex]` (single row if none). */
const circuitAround = (rows, rowIndex) => {
  let start = rowIndex;
  let end = rowIndex;
  while (start > 0 && isLinked(rows[start - 1], rows[start])) start--;
  while (end < rows.length - 1 && isLinked(rows[end], rows[end + 1])) end++;
  return { start, end };
};

/**
 * Decide whether new exercises inserted at `position` of `targetId` join the
 * target's circuit, without computing any orders.
 *
 * @returns {{ join: boolean, inCircuit: boolean, circuitSize: number,
 *   targetIsComplex: boolean }}
 */
export const resolveCircuitContext = ({
  exercises,
  sections = [],
  targetId,
  position,
}) => {
  const entries = buildEntries(exercises, sections);
  const rows = buildRows(entries);
  const rowIndex = rows.findIndex((row) => row.ids.includes(sid(targetId)));
  if (rowIndex === -1) throw new Error("insert target exercise not found");

  const target = rows[rowIndex];
  const { start, end } = circuitAround(rows, rowIndex);
  const circuitSize = end - start + 1;
  const inCircuit = circuitSize > 1;
  const k = rowIndex - start;
  const join =
    inCircuit &&
    !(position === "above" && k === 0) &&
    !(position === "below" && k === circuitSize - 1);
  return {
    join,
    inCircuit,
    circuitSize,
    targetIsComplex: target.isComplex,
    entries,
    rows,
    rowIndex,
    circuitStart: start,
    circuitEnd: end,
  };
};

/**
 * Plan an insert. Pure: callers generate ids and write the result.
 *
 * @param {object} params
 * @param {Array} params.exercises session exercises
 *   [{ _id, order, nextSuperset?, complex?, complexInstance? }]
 * @param {Array} params.sections session sections (notes are ignored)
 * @param {string} params.targetId exercise (or any complex member) to anchor on
 * @param {"above"|"below"} params.position
 * @param {Array} params.newItems items to insert, in order:
 *   { kind: "exercise", linkedToPrev?: boolean } | { kind: "section" }
 * @returns {{
 *   join: boolean,
 *   exerciseCount: number,
 *   items: Array,            // items actually inserted (sections dropped when joining)
 *   orders: Array<number>,   // order per item (same index as `items`)
 *   shifts: Array<{ kind, id, from, to }>,
 *   newNext: Array,          // per new EXERCISE (in order): null | { toNewIndex } | { toExistingId }
 *   patches: Array<{ id, previous, toNewIndex }>, // existing exercises re-pointed at a new one
 *   anchorIds: Array<string>, // ids of the target row (all complex members)
 *   layout: Array            // final order: { kind, id?, newItem?, newExercise? }
 * }}
 */
export const planInsert = ({
  exercises = [],
  sections = [],
  targetId,
  position,
  newItems = [],
}) => {
  const error = validateInsert({ targetExerciseId: targetId, position });
  if (error) throw new Error(error);

  const ctx = resolveCircuitContext({
    exercises,
    sections,
    targetId,
    position,
  });
  const { entries, rows, rowIndex, join } = ctx;
  const target = rows[rowIndex];

  let items = newItems.filter(
    (item) => item.kind === "exercise" || item.kind === "section"
  );
  if (join) items = items.filter((item) => item.kind === "exercise");
  // A section with no exercise after it would capture the existing exercises
  // that follow the insertion point — never insert one.
  let lastExerciseItem = -1;
  items.forEach((item, i) => {
    if (item.kind === "exercise") lastExerciseItem = i;
  });
  items = items.slice(0, lastExerciseItem + 1);

  const exerciseCount = items.filter((item) => item.kind === "exercise").length;
  if (exerciseCount === 0) throw new Error("nothing to insert");

  let insertIdx = position === "above" ? target.firstIdx : target.lastIdx + 1;
  // Imported sections going above an exercise must not land between its own
  // header and it (that leaves the header empty and moves the exercise into
  // the imported section): step back over the header(s) that precede it.
  if (position === "above" && items.some((item) => item.kind === "section")) {
    while (insertIdx > 0 && entries[insertIdx - 1].kind === "section")
      insertIdx--;
  }
  const before = entries.slice(0, insertIdx);
  const after = entries.slice(insertIdx);

  // --- orders for the new items ---------------------------------------
  const maxBefore = before.length
    ? Math.max(...before.map((entry) => entry.order))
    : null;
  let startOrder;
  if (maxBefore === null) {
    startOrder = after.find((entry) => entry.kind === "exercise").order;
  } else {
    startOrder = Math.floor(maxBefore) + 1;
  }
  const hasLeadingSection = items[0].kind === "section";
  if (
    hasLeadingSection &&
    maxBefore !== null &&
    maxBefore >= startOrder - 0.5
  ) {
    startOrder += 1; // keep the new header strictly after any header before
  }

  const orders = new Array(items.length);
  let exerciseIndex = 0;
  items.forEach((item, i) => {
    if (item.kind === "exercise") {
      orders[i] = startOrder + exerciseIndex;
      exerciseIndex++;
    }
  });
  items.forEach((item, i) => {
    if (item.kind === "section") {
      const nextExercise = items.findIndex(
        (other, j) => j > i && other.kind === "exercise"
      );
      orders[i] = orders[nextExercise] - 0.5;
    }
  });
  const newMax = Math.max(...orders);

  // --- shift everything at/after the insertion point as far as needed ---
  const firstAfter = after.length
    ? Math.min(...after.map((entry) => entry.order))
    : Number.POSITIVE_INFINITY;
  const shiftBy = Math.max(0, Math.floor(newMax - firstAfter) + 1);
  const shifts =
    shiftBy > 0
      ? after.map((entry) => ({
          kind: entry.kind,
          id: sid(entry.doc._id),
          from: entry.order,
          to: entry.order + shiftBy,
        }))
      : [];

  // --- superset links --------------------------------------------------
  const exerciseItemIdx = [];
  items.forEach((item, i) => {
    if (item.kind === "exercise") exerciseItemIdx.push(i);
  });
  const newNext = new Array(exerciseCount).fill(null);
  const patches = [];

  if (join) {
    for (let i = 0; i < exerciseCount - 1; i++)
      newNext[i] = { toNewIndex: i + 1 };
    if (position === "above") {
      const prevRow = rows[rowIndex - 1];
      patches.push({
        id: prevRow.ids[0],
        previous: sid(prevRow.docs[0].nextSuperset),
        toNewIndex: 0,
      });
      newNext[exerciseCount - 1] = { toExistingId: target.ids[0] };
    } else {
      patches.push({
        id: target.ids[0],
        previous: sid(target.docs[0].nextSuperset),
        toNewIndex: 0,
      });
      newNext[exerciseCount - 1] = { toExistingId: rows[rowIndex + 1].ids[0] };
    }
  } else {
    exerciseItemIdx.forEach((itemIdx, j) => {
      if (j > 0 && items[itemIdx].linkedToPrev) {
        // only link across consecutive exercises (never across a section)
        if (exerciseItemIdx[j - 1] === itemIdx - 1) {
          newNext[j - 1] = { toNewIndex: j };
        }
      }
    });
  }

  // --- final layout (for previews and tests) -----------------------------
  const exerciseNumberOfItem = new Map(
    exerciseItemIdx.map((idx, j) => [idx, j])
  );
  const layout = [
    ...before.map((entry) => ({ kind: entry.kind, id: sid(entry.doc._id) })),
    ...items.map((item, i) => ({
      kind: item.kind,
      newItem: i,
      ...(item.kind === "exercise"
        ? { newExercise: exerciseNumberOfItem.get(i) }
        : {}),
    })),
    ...after.map((entry) => ({ kind: entry.kind, id: sid(entry.doc._id) })),
  ];

  return {
    join,
    exerciseCount,
    items,
    orders,
    shifts,
    newNext,
    patches,
    anchorIds: target.ids,
    layout,
  };
};

/**
 * Build the `newItems` sequence for an import: the imported workout's
 * exercises (sorted by their original order) with its sections merged in by
 * original order — a section sits before the first exercise whose original
 * order is greater (same rule as remapPastedSectionOrders).
 *
 * @param {Array} importedExercises [{ _id, order, nextSuperset? }]
 * @param {Array} importedSections [{ order, ... }] (notes ignored)
 * @returns {Array} items: { kind: "exercise", sourceIndex, linkedToPrev } |
 *   { kind: "section", sourceIndex }  (sourceIndex indexes the input arrays)
 */
export const buildImportSequence = (
  importedExercises = [],
  importedSections = []
) => {
  const exercises = importedExercises
    .map((doc, sourceIndex) => ({ doc, sourceIndex }))
    .sort(
      (a, b) => orderOf(a.doc) - orderOf(b.doc) || a.sourceIndex - b.sourceIndex
    );
  const normalSections = importedSections
    .map((doc, sourceIndex) => ({ doc, sourceIndex }))
    .filter(({ doc }) => doc?.type !== "workout-note")
    .sort(
      (a, b) => orderOf(a.doc) - orderOf(b.doc) || a.sourceIndex - b.sourceIndex
    );

  const items = [];
  let sectionCursor = 0;
  exercises.forEach(({ doc, sourceIndex }, i) => {
    while (
      sectionCursor < normalSections.length &&
      orderOf(normalSections[sectionCursor].doc) < orderOf(doc)
    ) {
      items.push({
        kind: "section",
        sourceIndex: normalSections[sectionCursor].sourceIndex,
      });
      sectionCursor++;
    }
    const previous = exercises[i - 1]?.doc;
    items.push({
      kind: "exercise",
      sourceIndex,
      linkedToPrev:
        i > 0 &&
        items[items.length - 1]?.kind === "exercise" &&
        Boolean(previous?.nextSuperset) &&
        sid(previous.nextSuperset) === sid(doc._id),
    });
  });
  while (sectionCursor < normalSections.length) {
    items.push({
      kind: "section",
      sourceIndex: normalSections[sectionCursor].sourceIndex,
    });
    sectionCursor++;
  }
  return items;
};

/**
 * Preview the letters the new exercises (and the shifted existing ones) get
 * once inserted — the same A / A1 / B scheme the session list renders.
 *
 * @param {object} params same as planInsert
 * @returns {{
 *   plan: object,                    // planInsert result
 *   newLetters: Array<string>,       // letter per new exercise, in order
 *   letterOf: Object<string,string>, // letter per existing exercise id after the insert
 *   targetLetter: string             // target's letter after the insert
 * }}
 */
export const previewInsert = (params) => {
  const { exercises = [], sections = [], targetId } = params;
  const plan = planInsert(params);

  const patched = new Map(
    plan.patches.map((patch) => [String(patch.id), `new:${patch.toNewIndex}`])
  );
  const existingById = new Map(
    exercises.map((exercise) => [sid(exercise._id), exercise])
  );
  const sectionById = new Map(
    excludeWorkoutNotes(sections).map((section) => [sid(section._id), section])
  );

  const syntheticExercises = [];
  const syntheticSections = [];
  plan.layout.forEach((entry, position) => {
    if (entry.id !== undefined) {
      if (entry.kind === "section") {
        syntheticSections.push({
          ...sectionById.get(entry.id),
          order: position,
        });
      } else {
        const doc = existingById.get(entry.id);
        syntheticExercises.push({
          ...doc,
          _id: entry.id,
          order: position,
          nextSuperset:
            patched.get(entry.id) ?? sid(doc.nextSuperset) ?? undefined,
        });
      }
      return;
    }
    if (entry.kind === "section") {
      syntheticSections.push({
        _id: `new-section:${entry.newItem}`,
        order: position,
      });
      return;
    }
    const link = plan.newNext[entry.newExercise];
    syntheticExercises.push({
      _id: `new:${entry.newExercise}`,
      order: position,
      nextSuperset: link
        ? link.toExistingId
          ? sid(link.toExistingId)
          : `new:${link.toNewIndex}`
        : undefined,
    });
  });

  const prefixes = computeSectionPrefixes(
    syntheticExercises,
    syntheticSections
  );
  const newLetters = Array.from(
    { length: plan.exerciseCount },
    (_, i) => prefixes[`new:${i}`]
  );
  const letterOf = {};
  for (const exercise of exercises) {
    letterOf[sid(exercise._id)] = prefixes[sid(exercise._id)];
  }
  return { plan, newLetters, letterOf, targetLetter: letterOf[sid(targetId)] };
};

const sameSession = (a, b) =>
  Boolean(a && b) &&
  String(a.id) === String(b.id) &&
  Number(a.program) === Number(b.program) &&
  Number(a.programWeek) === Number(b.programWeek) &&
  Number(a.programSession) === Number(b.programSession);

/**
 * The target exercise plus every exercise/section of the same container:
 *  - program session (default): from the cycle-wide lists the program editor
 *    already loads (locals.editExercises / locals.editSections), matched on the
 *    target's `cycle` ref;
 *  - calendar day (`day`): from the calendar's lists (locals.activeExercises /
 *    locals.activeSections), matched on `utcDate`. Calendar exercises have no
 *    `cycle` of their own, and ones scheduled from a program share the day.
 *
 * @param {Array} editExercises
 * @param {Array} editSections
 * @param {string} targetId
 * @param {object} [options]
 * @param {string|number} [options.day] "YYYY-MM-DD" or YYYYMMDD
 * @returns {{ target: object, exercises: Array, sections: Array }|null}
 */
export const getInsertSession = (
  editExercises = [],
  editSections = [],
  targetId,
  { day } = {}
) => {
  const target = editExercises.find(
    (exercise) => sid(exercise._id) === sid(targetId)
  );
  if (!target) return null;

  if (day) {
    const utcDate = Number(String(day).replace(/-/g, ""));
    const onDay = (doc) => Number(doc.utcDate) === utcDate;
    if (!onDay(target)) return null;
    return {
      target,
      exercises: editExercises.filter(onDay),
      sections: editSections.filter(onDay),
    };
  }

  if (!target.cycle) return null;
  return {
    target,
    exercises: editExercises.filter((exercise) =>
      sameSession(exercise.cycle, target.cycle)
    ),
    sections: editSections.filter((section) =>
      sameSession(section.cycle, target.cycle)
    ),
  };
};

/**
 * Everything the insert UI shows for the current target / position / queue.
 *
 * @param {object} params
 * @param {Array} params.exercises session exercises
 * @param {Array} params.sections session sections
 * @param {string} params.targetId
 * @param {"above"|"below"} params.position
 * @param {Array} params.queueItems { kind: "exercise"|"section", linkedToPrev? }
 * @returns {{
 *   inCircuit: boolean, join: boolean, circuitSize: number,
 *   targetIsComplex: boolean,
 *   targetLetterNow: string, // the target's letter today (used in labels)
 *   targetLetter: string,   // the target's letter after the queued items are inserted
 *   newLetters: Array<string> // one per queued EXERCISE, in order
 * }}
 */
export const describeInsert = ({
  exercises,
  sections,
  targetId,
  position,
  queueItems = [],
}) => {
  const context = resolveCircuitContext({
    exercises,
    sections,
    targetId,
    position,
  });
  const base = {
    inCircuit: context.inCircuit,
    join: context.join,
    circuitSize: context.circuitSize,
    targetIsComplex: context.targetIsComplex,
  };
  const targetLetterNow = computeSectionPrefixes(exercises, sections)[
    sid(targetId)
  ];
  const hasExercise = queueItems.some((item) => item.kind === "exercise");
  if (!hasExercise) {
    return {
      ...base,
      targetLetterNow,
      targetLetter: targetLetterNow,
      newLetters: [],
    };
  }
  const preview = previewInsert({
    exercises,
    sections,
    targetId,
    position,
    newItems: queueItems.map((item) => ({
      kind: item.kind,
      linkedToPrev: Boolean(item.linkedToPrev),
    })),
  });
  return {
    ...base,
    targetLetterNow,
    targetLetter: preview.targetLetter,
    newLetters: preview.newLetters,
  };
};
